import { supabase } from "@/lib/supabase-client";
import { RoleType } from "@/types/db/roles";
import { RoleUserType } from "@/types/db/roles_users";

export const roleService = {
  async getRoles(): Promise<RoleType[] | null> {
    const { data, error } = await supabase
      .from("roles")
      .select(`id, tenant_id, active, name, description, meta_data, created_at`)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    // Load roles_users from link schema for each role
    const rolesWithUsers = await Promise.all(
      (data || []).map(async (role) => {
        const { data: rolesUsers } = await supabase
          .schema("link")
          .from("roles_users")
          .select("user_id, tenant_id")
          .eq("role_id", role.id);

        return {
          ...role,
          roles_users: rolesUsers || [],
        };
      }),
    );

    return rolesWithUsers as unknown as RoleType[];
  },

  async getRoleById(id: string): Promise<RoleType & { roles_users?: RoleUserType[] }> {
    const { data, error } = await supabase.from("roles").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }

    // Load roles_users from link schema
    const { data: rolesUsers } = await supabase
      .schema("link")
      .from("roles_users")
      .select("user_id, tenant_id")
      .eq("role_id", id);

    return {
      ...data,
      roles_users: rolesUsers || [],
    } as RoleType & { roles_users?: RoleUserType[] };
  },

  async addRole(roleData: { name: string; description?: string; meta_data?: any }): Promise<RoleType | null> {
    const { data, error } = await supabase.from("roles").insert([roleData]).select().single();
    if (error) {
      console.error("Error adding Role:", error.message);
      throw new Error(error.message);
    }
    return data as RoleType;
  },

  async saveRowPatches(patches: Partial<RoleType>[]): Promise<RoleType[]> {
    const { data, error } = await supabase.from("roles").upsert(patches, { onConflict: "id,tenant_id" }).select();

    if (error) throw error;
    return data as RoleType[];
  },

  async deleteRolesByIds(roleIds: string[]): Promise<boolean> {
    const { error } = await supabase.from("roles").delete().in("id", roleIds);
    if (error) {
      console.error("Error deleting Roles by IDs:", error.message);
      return false;
    }
    return true;
  },

  // Manage roles_users link table
  async addUsersToRole(roleId: string, tenantId: string, userIds: string[]): Promise<boolean> {
    const records = userIds.map((userId) => ({
      role_id: roleId,
      user_id: userId,
      tenant_id: tenantId,
    }));

    const { error } = await supabase.schema("link").from("roles_users").insert(records);
    if (error) {
      console.error("Error adding users to role:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async removeUsersFromRole(roleId: string, userIds: string[]): Promise<boolean> {
    const { error } = await supabase
      .schema("link")
      .from("roles_users")
      .delete()
      .eq("role_id", roleId)
      .in("user_id", userIds);

    if (error) {
      console.error("Error removing users from role:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async syncRoleUsers(roleId: string, tenantId: string, newUserIds: string[]): Promise<boolean> {
    // Get current users
    const { data: currentUsers } = await supabase
      .schema("link")
      .from("roles_users")
      .select("user_id")
      .eq("role_id", roleId);

    const currentUserIds = (currentUsers || []).map((u) => u.user_id);

    // Find users to add and remove
    const toAdd = newUserIds.filter((id) => !currentUserIds.includes(id));
    const toRemove = currentUserIds.filter((id) => !newUserIds.includes(id));

    // Remove users
    if (toRemove.length > 0) {
      await this.removeUsersFromRole(roleId, toRemove);
    }

    // Add users
    if (toAdd.length > 0) {
      await this.addUsersToRole(roleId, tenantId, toAdd);
    }

    return true;
  },
};
